import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { privatize } from '@/lib/utils/revenueUtils'
import { formatDeviceLabel } from '@/lib/utils/utmLabel'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { SegmentedControl } from '@/components/shared/layout'
import { LoadingState } from '@/components/shared/layout'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon, InformationCircleIcon } from '@hugeicons/core-free-icons'
import { clickDropCopy, fmtMoney, landingLabel } from './attributionView'
import { GoogleAdsMark, MetaMark } from './AttributionParts'
import type { AdsAttributionCampaign, AdsAttributionData, CampaignBreakdown } from './hooks/useAdsAttribution'

/**
 * Campaign section with goal selector + dimension split comparison.
 *
 * Goals: Revenue (ROAS bar), Trials, Forms, Page visits (efficiency bar vs target).
 * Split by: None · Device · Country · Browser — shows global first, then segments
 * below each campaign card.
 */

export type GoalType    = 'revenue' | 'trials' | 'forms' | 'visits'
export type SplitDim    = 'none'    | 'device'  | 'country' | 'browser'

const MIN_TTC_SAMPLES = 5   // below this, don't show TTC
const ROAS_MAX = 4           // bar scale for revenue goal

interface GoalConfig {
  label: string
  count:  (c: AdsAttributionCampaign) => number
  /** $ per outcome, null when no outcomes */
  cpo:    (c: AdsAttributionCampaign) => number | null
  /** Default target cost — user-editable later */
  defaultTarget: number | null
  outcomeLabel: string   // "customers", "trials", "form submits", "visits"
  isRevenue: boolean
}

const GOALS: Record<GoalType, GoalConfig> = {
  revenue: {
    label: 'Revenue',
    count:  (c) => c.revenue_customers ?? c.revenue_conversions,
    cpo:    (c) => c.true_cpa,
    defaultTarget: null,
    outcomeLabel: 'customers',
    isRevenue: true,
  },
  trials: {
    label: 'Trials',
    count:  (c) => c.trial_count,
    cpo:    (c) => c.cost_per_trial,
    defaultTarget: null,
    outcomeLabel: 'trials',
    isRevenue: false,
  },
  forms: {
    label: 'Forms',
    count:  (c) => c.form_submissions ?? 0,
    cpo:    (c) => {
      const n = c.form_submissions ?? 0
      return n > 0 ? Math.round(c.spend / n * 100) / 100 : null
    },
    defaultTarget: null,
    outcomeLabel: 'form submits',
    isRevenue: false,
  },
  visits: {
    label: 'Visits',
    count:  (c) => c.sessions,
    cpo:    (c) => c.sessions > 0 ? Math.round(c.spend / c.sessions * 100) / 100 : null,
    defaultTarget: null,
    outcomeLabel: 'visits',
    isRevenue: false,
  },
}

const SPLIT_OPTIONS: { value: SplitDim; label: string }[] = [
  { value: 'none',    label: 'No split'  },
  { value: 'device',  label: 'Device'    },
  { value: 'country', label: 'Country'   },
  { value: 'browser', label: 'Browser'   },
]

const SPLIT_KEYS: Record<SplitDim, keyof CampaignBreakdown | null> = {
  none:    null,
  device:  'devices',
  country: 'countries',
  browser: 'browsers',
}

// ── Small helpers ──────────────────────────────────────────────────────────────

const money = (n: number, priv = false) =>
  privatize(n === 0 ? '$0' : n < 10 ? '$' + n.toFixed(2) : '$' + n.toLocaleString(), priv)

function PlatformMark({ platforms }: { platforms: string[] }) {
  const p = (platforms[0] ?? '').toLowerCase()
  if (p.includes('google')) return <GoogleAdsMark />
  if (p.includes('meta') || p.includes('facebook') || p.includes('instagram')) return <MetaMark />
  return <span className="size-4 shrink-0" />
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span className={cn(
      'inline-flex h-[18px] items-center rounded-[4px] px-1.5 text-[10px] font-semibold uppercase tracking-[0.06em]',
      active ? 'bg-live/15 text-live' : 'bg-muted text-muted-foreground',
    )}>
      {active ? 'Active' : 'Paused'}
    </span>
  )
}

// ── Dimension breakdown rows ───────────────────────────────────────────────────

function DimensionBreakdown({
  items, totalSessions, dim,
}: {
  items: { name: string; sessions: number }[]
  totalSessions: number
  dim?: SplitDim
}) {
  const shown = items.slice(0, 5)
  return (
    <div className="mt-3 space-y-1.5">
      {/* Global row first */}
      <div className="flex items-center gap-3 text-xs font-medium text-foreground">
        <span className="w-28 shrink-0 truncate">Everyone</span>
        <div className="relative flex-1 overflow-hidden rounded-full bg-muted" style={{ height: 6 }}>
          <div className="h-full rounded-full bg-primary/40" style={{ width: '100%' }} />
        </div>
        <span className="w-8 shrink-0 text-right tabular-nums">{formatNumeric(totalSessions)}</span>
        <span className="w-7 shrink-0 text-right tabular-nums text-muted-foreground">100%</span>
      </div>
      {/* Segments */}
      {shown.map((item) => {
        const pct = totalSessions > 0 ? (item.sessions / totalSessions) * 100 : 0
        return (
          <div key={item.name} className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="w-28 shrink-0 truncate">
              {dim === 'device' ? formatDeviceLabel(item.name) : (item.name || 'Unknown')}
            </span>
            <div className="relative flex-1 overflow-hidden rounded-full bg-muted" style={{ height: 6 }}>
              <div className="h-full rounded-full bg-primary/30 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 shrink-0 text-right tabular-nums">{formatNumeric(item.sessions)}</span>
            <span className="w-7 shrink-0 text-right tabular-nums">{Math.round(pct)}%</span>
          </div>
        )
      })}
    </div>
  )
}

// ── Campaign card ──────────────────────────────────────────────────────────────

function CampaignCard({
  c,
  goal,
  splitDim,
  breakdown,
  privacyMode,
  needsRevenue,
  renewedDollars,
  expanded,
  onToggle,
}: {
  c: AdsAttributionCampaign
  goal: GoalType
  splitDim: SplitDim
  breakdown: CampaignBreakdown | undefined
  privacyMode: boolean
  needsRevenue: boolean
  renewedDollars: number
  expanded: boolean
  onToggle: () => void
}) {
  const g = GOALS[goal]
  const count = g.count(c)
  const cpo   = g.cpo(c)
  const priv  = privacyMode

  // Revenue: ROAS bar (0→ROAS_MAX, break-even at 1)
  // CPX: efficiency bar (0→target, short=efficient, over=amber)
  const revenueEarned = Math.max(0, c.revenue - renewedDollars)
  const effectiveRoas = c.spend > 0 ? revenueEarned / c.spend : 0
  const roasBarW = Math.min(effectiveRoas / ROAS_MAX, 1) * 100
  const isWinning = effectiveRoas >= 1

  const noMatch = c.sessions === 0 && c.clicks > 0   // UTM problem

  // Sub-line: landing page or click-drop warning
  const drop = clickDropCopy(c.click_to_session_rate, c.top_landing_page || c.target_urls?.[0])
  const landing = !drop ? landingLabel(c.top_landing_page || c.target_urls?.[0]) : null

  return (
    <div
      className={cn(
        'cursor-pointer rounded-xl border transition-colors hover:border-border/60',
        g.isRevenue && isWinning && !needsRevenue ? 'border-primary/20 bg-primary-tint/10' : 'border-border/30 bg-card',
      )}
      onClick={onToggle}
    >
      <div className="flex flex-col gap-4 p-5">

        {/* ── Header: name + primary metric ── */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-2">
              <PlatformMark platforms={c.platforms} />
              <span className="truncate text-[13px] font-medium">{c.campaign}</span>
              <ActiveBadge active={(c as any).active ?? true} />
            </div>
            <div className="pl-6 text-xs text-muted-foreground">
              {noMatch ? (
                <span className="text-warning">0 matched · {c.clicks} clicked (UTM missing)</span>
              ) : drop ? (
                <span className="text-warning">{drop}</span>
              ) : landing ? (
                <span>{landing}</span>
              ) : null}
            </div>
          </div>

          {/* Primary metric value */}
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            {g.isRevenue ? (
              needsRevenue ? (
                <span className="text-sm font-medium text-muted-foreground">Needs {needsRevenue}</span>
              ) : c.revenue > 0 ? (
                // Revenue matched — show earned prominently
                <>
                  <span className="text-xl font-bold tabular-nums text-primary-strong">
                    {privatize(money(revenueEarned), priv)}
                  </span>
                  <span className="text-xs text-muted-foreground">{money(c.spend, priv)} spent</span>
                </>
              ) : (
                // No revenue matched — show spend as the primary number, no "—" placeholder
                <span className="text-sm font-medium text-muted-foreground">
                  {money(c.spend, priv)} spent
                </span>
              )
            ) : (
              <>
                <span className={cn('text-xl font-bold tabular-nums', count > 0 ? 'text-foreground' : 'text-muted-foreground')}>
                  {count > 0 ? formatNumeric(count) : '0'}
                </span>
                <span className="text-xs text-muted-foreground">{g.outcomeLabel}</span>
              </>
            )}
          </div>
        </div>

        {/* ── Visual bar ── */}
        {g.isRevenue ? (
          /* ROAS bar */
          <div>
            <div className="relative h-3 overflow-hidden rounded-full bg-muted">
              <div className="absolute top-0 h-full w-px bg-foreground/20" style={{ left: `${(1 / ROAS_MAX) * 100}%` }} />
              {c.revenue > 0 && !needsRevenue && (
                <div
                  className={cn('h-full rounded-full transition-all', isWinning ? 'bg-primary' : 'bg-warning/60')}
                  style={{ width: `${roasBarW}%` }}
                />
              )}
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
              <span>$0</span>
              <span>break even</span>
              <span>{ROAS_MAX}×</span>
            </div>
            {c.revenue > 0 && !needsRevenue && (
              <p className={cn('mt-1.5 text-xs font-medium', isWinning ? 'text-primary-strong' : 'text-warning')}>
                {effectiveRoas.toFixed(2)}× return
                {renewedDollars > 0 && (
                  <span className="ml-1.5 font-normal text-muted-foreground">
                    ({money(renewedDollars, priv)} renewals not counted)
                  </span>
                )}
              </p>
            )}
            {c.revenue === 0 && !needsRevenue && (
              <p className="mt-1.5 text-xs text-muted-foreground">No revenue matched — check attribution setup</p>
            )}
          </div>
        ) : (
          /* CPX efficiency bar */
          <div>
            {cpo !== null ? (
              <p className="text-sm font-medium tabular-nums">
                {money(cpo, priv)} <span className="text-xs font-normal text-muted-foreground">per {g.outcomeLabel} · {money(c.spend, priv)} spent</span>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">0 {g.outcomeLabel} tracked from this campaign</p>
            )}
            {/* Cost bar: raw spend divided by outcome count */}
            {count > 0 && (
              <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary/60" style={{ width: `${Math.min((count / Math.max(c.clicks, 1)) * 100 * 3, 100)}%` }} />
              </div>
            )}
          </div>
        )}

        {/* ── Time to convert (per-campaign, only when significant) ── */}
        {c.avg_hours_to_convert != null && (c as any).ttc_sample_size >= 3 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <HugeiconsIcon icon={InformationCircleIcon} size={12} />
            <span>Avg. {(c.avg_hours_to_convert / 24).toFixed(1)} days to convert · {(c as any).ttc_sample_size} customers</span>
          </div>
        )}

        {/* ── Dimension split ── */}
        {splitDim !== 'none' && breakdown && (
          <div className="border-t border-border/20 pt-3">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Visits by {splitDim}
            </p>
            <DimensionBreakdown
              items={(breakdown[SPLIT_KEYS[splitDim] as keyof CampaignBreakdown] as { name: string; sessions: number }[]) ?? []}
              totalSessions={c.sessions}
              dim={splitDim}
            />
          </div>
        )}

        {/* ── Expanded detail ── */}
        {expanded && (
          <div className="grid grid-cols-4 gap-3 border-t border-border/20 pt-3 text-xs">
            {[
              ['Clicks (platform)', formatNumeric(c.clicks)],
              ['Visitors tracked', c.sessions > 0 ? formatNumeric(c.sessions) : <span className="text-warning">0 matched</span>],
              ['Impressions', formatNumeric(c.impressions)],
              ['Click → land', c.click_to_session_rate != null ? (c.click_to_session_rate * 100).toFixed(0) + '%' : '—'],
              ['Trials', formatNumeric(c.trial_count)],
              ['Form submits', formatNumeric(c.form_submissions ?? 0)],
              ['$/customer', c.true_cpa != null ? money(c.true_cpa, priv) : '—'],
              ['$/trial', c.cost_per_trial != null ? money(c.cost_per_trial, priv) : '—'],
            ].map(([k, v]) => (
              <div key={String(k)}>
                <span className="text-[10px] text-muted-foreground">{k}</span>
                <p className="mt-0.5 font-medium">{v}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Time-to-convert tile ───────────────────────────────────────────────────────

export function TimeToConvertTile({
  avgHours,
  sampleSize,
  privacyMode = false,
}: {
  avgHours: number | null
  sampleSize: number
  privacyMode?: boolean
}) {
  if (avgHours == null || sampleSize < MIN_TTC_SAMPLES) return null
  const days = avgHours / 24
  const label = days < 1
    ? `${Math.round(avgHours)}h`
    : days < 2
    ? `${days.toFixed(1)} days`
    : `${Math.round(days)} days`

  return (
    <div className="flex flex-col gap-1 rounded-xl border border-border/30 bg-card px-4 py-3.5">
      <span className="text-xs text-muted-foreground">Avg. time to convert</span>
      <span className={cn('text-[22px] font-semibold tabular-nums tracking-tight', privacyMode && 'blur-sm select-none')}>
        {label}
      </span>
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <span>click → payment</span>
        <span className="mx-1 h-3 w-px bg-border/60" />
        <span>based on {sampleSize} customers</span>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AttributionCampaigns({
  adsAttributionData,
  isLoading,
  error,
  privacyMode = false,
  needsRevenue = null,
  renewalsByCampaign,
  goal: controlledGoal,
  onGoalChange,
}: {
  adsAttributionData: AdsAttributionData | null
  isLoading: boolean
  error?: string | null
  privacyMode?: boolean
  needsRevenue?: string | null
  renewalsByCampaign?: Map<string, number> | null
  /** When provided, the goal selector is hidden (the parent owns it). */
  goal?: GoalType
  onGoalChange?: (g: GoalType) => void
}) {
  const [internalGoal, setInternalGoal] = useState<GoalType>('revenue')
  const goal   = controlledGoal ?? internalGoal
  const setGoal = (g: GoalType) => {
    if (onGoalChange) onGoalChange(g)
    else setInternalGoal(g)
  }
  const [splitDim, setSplit] = useState<SplitDim>('none')
  const [expanded, setExp]   = useState<string | null>(null)
  const [showOtherTagged, setShowOtherTagged] = useState(false)

  const { campaigns = [], unmatched_ad_campaigns = [], unmatched_utm_campaigns = [], breakdowns = {} } = adsAttributionData ?? {}

  // Which goals have any data (to style unavailable goals differently)
  const goalsAvailable = useMemo<Record<GoalType, boolean>>(() => ({
    revenue: true,
    trials:  campaigns.some(c => c.trial_count > 0),
    forms:   campaigns.some(c => (c.form_submissions ?? 0) > 0),
    visits:  campaigns.some(c => c.sessions > 0),
  }), [campaigns])

  const GOAL_OPTIONS = (Object.keys(GOALS) as GoalType[]).map(k => ({
    value: k,
    label: GOALS[k].label,
  }))

  if (isLoading && !adsAttributionData) {
    return <div className="py-12"><LoadingState message="Loading campaigns…" size="sm" /></div>
  }

  if (!adsAttributionData && error) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        <p className="mb-1 text-foreground">Campaigns couldn't be loaded</p>
        <p>Refresh to try matching campaigns again.</p>
      </div>
    )
  }

  if (!adsAttributionData) {
    return <div className="py-12 text-center text-sm text-muted-foreground">No ad platforms connected</div>
  }

  const untaggedSpend = unmatched_ad_campaigns.reduce((s, c) => s + (c.spend || 0), 0)

  return (
    <div className="flex flex-col gap-3">

      {/* ── Controls: goal + split ── */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Show "Measure by" only when the parent isn't controlling the goal */}
        {!onGoalChange && (
          <>
            <span className="text-xs text-muted-foreground shrink-0">Measure by</span>
            <SegmentedControl<GoalType>
              options={GOAL_OPTIONS}
              value={goal}
              onChange={setGoal}
            />
          </>
        )}
        <span className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Compare by</span>
          <SegmentedControl<SplitDim>
            options={SPLIT_OPTIONS}
            value={splitDim}
            onChange={setSplit}
          />
        </span>
      </div>

      {/* Goal not available nudge */}
      {!goalsAvailable[goal] && goal !== 'revenue' && (
        <div className="flex items-center gap-2.5 rounded-lg border border-border/30 bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
          <HugeiconsIcon icon={InformationCircleIcon} size={13} />
          No {GOALS[goal].outcomeLabel} tracked from these campaigns yet.
          {goal === 'forms' && ' Make sure forms on your landing pages have been visited by ad traffic.'}
        </div>
      )}

      {/* ── Campaign cards ── */}
      <div className={cn('grid gap-3', campaigns.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1')}>
        {campaigns.map((c) => (
          <CampaignCard
            key={c.utm_campaign}
            c={c}
            goal={goal}
            splitDim={splitDim}
            breakdown={breakdowns[c.utm_campaign]}
            privacyMode={privacyMode}
            needsRevenue={needsRevenue !== null}
            renewedDollars={renewalsByCampaign?.get(c.utm_campaign) ?? 0}
            expanded={expanded === c.utm_campaign}
            onToggle={() => setExp(expanded === c.utm_campaign ? null : c.utm_campaign)}
          />
        ))}
      </div>

      {/* ── Untagged campaigns notice ── */}
      {unmatched_ad_campaigns.length > 0 && (
        <div className="flex flex-col overflow-hidden rounded-xl border border-warning/20 bg-warning-tint/40">
          <button
            className="flex items-center gap-2.5 px-4 py-3 text-left hover:bg-warning-tint/60"
            onClick={() => setShowOtherTagged(v => !v)}
          >
            <span className="size-1.5 shrink-0 rounded-full bg-warning" />
            <span className="flex-1 text-[13px] leading-4 text-warning">
              {unmatched_ad_campaigns.length} {unmatched_ad_campaigns.length === 1 ? 'campaign' : 'campaigns'} spent{untaggedSpend > 0 ? ` ${money(untaggedSpend, privacyMode)}` : ''} but no tagged visits arrived. Usually a missing utm_campaign.
            </span>
            <span className="text-xs font-medium text-warning">{showOtherTagged ? 'Hide' : 'Fix tags ›'}</span>
          </button>
          {showOtherTagged && unmatched_ad_campaigns.map((c) => {
            const firstUrl = c.target_urls?.[0] ?? null
            let utm: string | null = null
            try { utm = firstUrl ? new URL(firstUrl).searchParams.get('utm_campaign') : null } catch { utm = null }
            return (
              <div key={c.campaign} className="flex items-center gap-3 border-t border-warning/10 bg-warning-tint/20 px-4 py-3 text-sm">
                <PlatformMark platforms={c.platforms} />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium">{c.campaign}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {utm ? `Ad URL tags utm_campaign=${utm}` : 'Ad URL has no utm_campaign'}
                    {firstUrl ? ` · ${landingLabel(firstUrl)}` : ''}
                  </span>
                </div>
                <span className="ml-auto text-xs tabular-nums">{money(c.spend, privacyMode)}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* ── UTM-only campaigns (collapsed, not cluttering the main view) ── */}
      {unmatched_utm_campaigns.length > 0 && (
        <details className="group">
          <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg border border-border/30 px-4 py-3 text-xs text-muted-foreground hover:bg-muted/30">
            <span className="size-1.5 shrink-0 rounded-full bg-border" />
            <span className="flex-1">
              {unmatched_utm_campaigns.length} other tagged {unmatched_utm_campaigns.length === 1 ? 'source' : 'sources'} not from a connected ad account (newsletters, email, organic UTMs)
            </span>
            <HugeiconsIcon icon={ArrowRight01Icon} size={12} className="shrink-0 transition-transform group-open:rotate-90" />
          </summary>
          <div className="mt-1 divide-y divide-border/20 rounded-lg border border-border/30 bg-card">
            {unmatched_utm_campaigns.map((c) => (
              <div key={c.utm_campaign} className="flex items-center gap-3 px-4 py-3 text-xs">
                <span className="size-4 shrink-0" />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="font-medium">{c.utm_campaign}</span>
                  <span className="text-muted-foreground">
                    {formatNumeric(c.sessions)} {c.sessions === 1 ? 'visit' : 'visits'} tagged
                    {c.top_landing_page && c.top_landing_page !== 'null' ? ` · ${landingLabel(c.top_landing_page)}` : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}
